import { use, useCallback, useState } from 'react'

import { TextWithBullet } from '@chat/Dialog/MessageItem/ContextMenu/TextWithBullet'
import { MarkAsSafeContainerProps } from '@chat/Dialog/MessageItem/ContextMenu/types'

import { Trans, useTranslation } from 'react-i18next'

import { useSearchParams, useNavigate } from 'react-router-dom'

import { fetchMessageById } from '@app/api'
import { markMessageAsSpam } from '@app/api/spamDetection/markMessageAsSpam'
import { ChatContext } from '@app/context'
import { useConversationActorRole, useWhitelist } from '@app/hooks'

import { useNotificationsActions } from '@app/hooks/useNotificationsActions'
import { MarkAsSpamReason } from '@app/models'

import { trackMarkMessageAsSpam } from '@app/services/tracking'

import { Button, Loader, RadioItem } from '@lib/components'

import { handleConversationStatusChange } from './handleConversationStatusChange'
import styles from './styles.module.scss'

export const MarkAsSpamContainer = ({
  conversationId,
  message,
  onClose,
  abortController,
}: MarkAsSafeContainerProps) => {
  const { dispatch } = use(ChatContext)
  const conversationActorRole = useConversationActorRole()
  const { t } = useTranslation()
  const { showError } = useNotificationsActions()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const { isWhitelisted, fetching: fetchingWhitelist } = useWhitelist(
    conversationId,
    message
  )
  const [busy, setBusy] = useState(false)
  const [markAsSpamReason, setMarkAsSpamReason] = useState<
    MarkAsSpamReason | undefined
  >(undefined)
  const handleConfirm = useCallback(async () => {
    setBusy(true)
    const signal = abortController()?.signal
    try {
      await markMessageAsSpam(
        conversationId,
        message.id,
        markAsSpamReason!,
        signal
      )
      trackMarkMessageAsSpam(conversationId, message.id, conversationActorRole)
      const msg = await fetchMessageById(conversationId, message.id, signal)
      dispatch({
        type: 'MESSAGE_UPDATED',
        payload: { conversationId, message: msg.message },
      })
      handleConversationStatusChange(conversationId, searchParams, navigate, dispatch)
    } catch (e) {
      showError()
      console.error('Error marking message as spam', e)
    } finally {
      onClose()
    }
  }, [markAsSpamReason])

  if (fetchingWhitelist) {
    return <Loader />
  }

  const spamReasons: MarkAsSpamReason[] = ['Spam', 'Phishing', 'IllegalContent']
  return isWhitelisted ? (
    <div className={styles.textContainer}>
      <div className={styles.textMain}>
        {t('Chat.Message.CannotMarkAsSpamDueToWhitelist')}
      </div>
      <div className={styles.textSecondary}>
        {t('Chat.Message.ContactCustosToRemoveFromWhitelist')}
      </div>
    </div>
  ) : (
    <>
      <div className={styles.textContainer}>
        <div className={styles.textMain}>
          <Trans i18nKey="Chat.Message.MarkAsSpamConfirm">
            <TextWithBullet> </TextWithBullet>
          </Trans>
        </div>
        <div className={styles.reasonsContainer}>
          {spamReasons.map((reason) => (
            <RadioItem<MarkAsSpamReason | undefined>
              value={reason}
              name={'spamReason'}
              onValueChange={setMarkAsSpamReason}
              key={reason}
            >
              <Trans i18nKey={`Chat.Message.SpamReason.${reason}`}>
                {reason}
              </Trans>
            </RadioItem>
          ))}
        </div>
      </div>
      <div className={styles.buttons}>
        <Button variant={'secondary'} onClick={onClose}>
          {t('Common.Button.Cancel')}
        </Button>
        <Button
          variant={'primary'}
          className={busy ? styles.busy : ''}
          disabled={busy || !markAsSpamReason}
          onClick={handleConfirm}
        >
          {t('Common.Button.Confirm')}
        </Button>
      </div>
    </>
  )
}
